import assign from 'object-assign';
import qs from 'querystringify';
import requests from 'requests';
import jsonp from 'jsonp';
import diagnostics from 'diagnostics';

const log = diagnostics('request');

/**
 * Safely parse JSON and log on error
 *
 * @param {String} data String containing JSON data to parse.
 * @returns {Object} Data parsed into an object.
 * @api private
 */
function jsonparse(data) {
  try {
    return JSON.parse(data);
  } catch (error) {
    log(error.message);
  }
}

/**
 * Safely stringify an object into JSON and log on error
 *
 * @param {Object} obj An object to stringify into JSON.
 * @returns {String} A JSON string representing obj.
 * @api private
 */
function jsonstringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    log(error.message);
  }
}

/**
 * Process response.
 *
 * @param {Requests} request Requests instance.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api private
 */
function decoder(request, done) {
  var destroyed = false, buffer = [];

  request.on('data', function received(chunk) {
    buffer.push(chunk);
  });

  request.once('destroy', function destroy() {
    destroyed = true;
  });

  request.once('end', function end(error, status) {
    let data;

    //
    // There are some API's that return a 204 no-content, so there's nothing to
    // return and parse.
    //
    if (buffer.length) {
      data = buffer.join('');
      data = jsonparse(data) || data;
    }

    //
    // Clean up the buffer before processing the request to release additional memory.
    //
    buffer.length = 0;

    if (!error && status && status.code >= 400) {
      const { message = 'Received an error response from the server' } = data || {};
      error = new Error(message);
      error.status = status.code;
    }

    if (error) {
      if (destroyed) {
        return done(new Error('Request destroyed'));
      }

      error.data = data;
      return done(error);
    }

    done(null, data);
  });

  return request;
}

/**
 * Send the request and process response.
 *
 * @param {String} url URI to request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api private
 */
function send(url, options, done) {
  if (typeof options === 'function') {
    done = options;
    options = {};
  }

  options = assign({
    timeout: 30000,
    method: 'GET',
    headers: {},
    query: {}
  }, options);

  url = url + qs.stringify(options.query, true);

  if (options.jsonp || !('withCredentials' in requests[requests.method]())) {
    return jsonp(url, options, done);
  }

  if (options.body && typeof options.body !== 'string') {
    options.body = jsonstringify(options.body);
  }

  return decoder(requests(url, options), done);
}

/**
 * Execute DELETE request
 *
 * @param {String} url URI to request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api public
 */
export function del(url, options, done) {
  return send(url, assign({}, options, {
    method: 'DELETE'
  }), done);
}

/**
 * Execute GET request
 *
 * @param {String} url URI to request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api public
 */
export function get(url, options, done) {
  return send(url, options, done);
}

/**
 * Execute PATCH request
 *
 * @param {String} url URI to request.
 * @param {Object} data Data to send with request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api public
 */
export function patch(url, data, options, done) {
  if (typeof options === 'function') {
    done = options;
    options = {};
  }

  return send(url, assign({}, options, {
    method: 'PATCH',
    body: data
  }), done);
}

/**
 * Execute POST request
 *
 * @param {String} url URI to request.
 * @param {Object} data Data to send with request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api public
 */
export function post(url, data, options, done) {
  if (typeof options === 'function') {
    done = options;
    options = {};
  }

  return send(url, assign({}, options, {
    method: 'POST',
    body: data
  }), done);
}

/**
 * Execute PUT request
 *
 * @param {String} url URI to request.
 * @param {Object} data Data to send with request.
 * @param {Object} options Configuration.
 * @param {Function} done Completion callback.
 * @returns {Requests} Requests instance.
 * @api public
 */
export function put(url, data, options, done) {
  if (typeof options === 'function') {
    done = options;
    options = {};
  }

  return send(url, assign({}, options, {
    method: 'PUT',
    body: data
  }), done);
}

export default {
  del,
  get,
  patch,
  post,
  put
};



// WEBPACK FOOTER //
// ./node_modules/@godaddy/request/src/request.js